import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, UserPlus2, AtSign, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const KIND_ICON = { follow: UserPlus2, mention: AtSign };
const KIND_COLOR = { follow: "#34d399", mention: "#a78bfa" };

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, (Date.now() - t) / 1000);
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Bell icon for the navbar. Shows an unread count badge. Clicking opens a
 * dropdown with the latest 30 notifications and a "Mark all read" action.
 * Polls every 60 seconds while mounted.
 */
export default function NotificationsBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    if (!user) return;
    try {
      const r = await api.get("/me/notifications", { params: { limit: 30 } });
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch {
      /* anon — silently ignore */
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user?.id]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!user) return null;

  const markAllRead = async () => {
    if (unread === 0) return;
    try {
      await api.post("/me/notifications/read-all");
      setItems((p) => p.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      try { await api.post(`/me/notifications/${n.id}/read`); } catch { /* ignore */ }
      setItems((p) => p.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.ref_route) navigate(n.ref_route);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="notifications-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center justify-center w-9 h-9 text-zinc-400 hover:text-white transition-colors"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#ff3b30] text-white text-[9px] font-mono font-bold rounded-full flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-dropdown"
          className="absolute right-0 mt-2 w-[320px] sm:w-[360px] bg-[#0a0a0a] border border-[#a78bfa]/40 shadow-2xl shadow-black/60 max-h-[70vh] overflow-y-auto z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa]">
              // NOTIFICATIONS
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="notifications-mark-all-read"
                className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 hover:text-[#a78bfa] uppercase inline-flex items-center gap-1"
              >
                <Check size={11} /> Mark read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center font-mono text-xs tracking-wider text-zinc-500">
              // ALL QUIET — NO NEW SIGNALS
            </div>
          ) : (
            <ul>
              {items.map((n) => {
                const Icon = KIND_ICON[n.kind] || Bell;
                const color = KIND_COLOR[n.kind] || "#a78bfa";
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openItem(n)}
                      data-testid={`notification-${n.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors flex gap-3 ${
                        n.is_read ? "" : "bg-[#a78bfa]/[0.05]"
                      }`}
                    >
                      <div className="shrink-0 p-1.5 " style={{ backgroundColor: `${color}1a`, color }}>
                        <Icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[11px] text-white tracking-[0.05em] truncate">
                          {n.ctx}
                        </div>
                        {n.ref_text && (
                          <div className="text-[11px] text-zinc-400 leading-snug mt-0.5 line-clamp-2 italic">
                            “{n.ref_text}”
                          </div>
                        )}
                        <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-600 mt-1">
                          {relTime(n.created_at)} {n.is_read ? "" : "· NEW"}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {items.length > 0 && (
            <div className="px-4 py-2 border-t border-white/10 text-center sticky bottom-0 bg-[#0a0a0a]">
              <Link
                to={`/u/${user.username}`}
                onClick={() => setOpen(false)}
                className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 hover:text-[#a78bfa] uppercase"
              >
                See your profile →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
